"use client";

import { useLanguage } from "@tillix/i18n/react";
import { Card } from "@tillix/ui-atoms/card";
import { useAppointmentT } from "../i18n/use-t";
import {
  formatDateLabel,
  formatTimeLabel,
  isoToCompactDate,
  isoToCompactTime,
} from "../lib/sap-format";
import { getOrderRestrictionKey } from "../lib/status";
import { StatusBadge } from "./StatusBadge";
import type { OrderDetail } from "../types";

interface OrderDetailsProps {
  order: OrderDetail;
  canReschedule: boolean;
  canCancel: boolean;
}

export default function OrderDetails({
  order,
  canReschedule,
  canCancel,
}: OrderDetailsProps) {
  const t = useAppointmentT();
  const { language } = useLanguage();
  const date = formatDateLabel(
    isoToCompactDate(order.scheduleStartDate),
    language,
  );
  const start = formatTimeLabel(
    isoToCompactTime(order.scheduleStartTime),
    language,
  );
  const end = formatTimeLabel(
    isoToCompactTime(order.scheduleEndTime),
    language,
  );
  const restrictionKey = getOrderRestrictionKey({ canCancel, canReschedule });

  return (
    <Card>
      <Card.Header>
        <div className="flex items-start justify-between gap-4 max-md:flex-col">
          <Card.Title>
            {t("order.number", { orderNumber: order.orderNumber })}
          </Card.Title>
          <div className="flex flex-col items-end gap-2">
            <StatusBadge status={order.status} />
          </div>
        </div>
        <Card.Description>
          {t("order.scheduledForWindow", { date, start, end })}
        </Card.Description>
      </Card.Header>
      {restrictionKey && (
        <Card.Content className="text-muted-foreground text-sm">
          {t(restrictionKey)}
        </Card.Content>
      )}
    </Card>
  );
}
